import type { AxiosInstance } from 'axios';
import Connector from '@nextories/vue-core-components/src/utils/Connector';
import type {
  CurrentUser,
  Department,
  Flux,
  LoginResponse,
  Page,
  Region,
  RegionChange,
  RegionPayload,
} from '@/types/One';

type Queries = Record<string, string>;

// Lists are small reference catalogues: one page holds all of them
const PAGE_SIZE = '200';

function listUrl(path: string, queries: Queries): string {
  return `${path}?${new URLSearchParams({ page_size: PAGE_SIZE, ...queries })}`;
}

export default class OneApi extends Connector {
  constructor(http: AxiosInstance, isSendAllResponse = true) {
    // Connector types its axios instance as a string
    super(http as unknown as string, isSendAllResponse);
  }

  login(credentials: { user_name: string; password: string }): Promise<LoginResponse> {
    return this._methodData('post', 'login', credentials);
  }

  logout(refresh: string | null): Promise<{ detail: string }> {
    return this._methodData('post', 'logout', refresh ? { refresh } : {});
  }

  me(): Promise<CurrentUser> {
    return this._methodData('get', 'me');
  }

  resetPasswordRequest(request: { user_name?: string; email?: string }): Promise<{ detail: string }> {
    return this._methodData('post', 'resetPasswordRequest', request);
  }

  resetPassword(request: { token: string; password: string; password_confirm: string }): Promise<{ detail: string }> {
    return this._methodData('post', 'resetPassword', request);
  }

  getRegions(queries: Queries = {}): Promise<Page<Region>> {
    return this._methodData('get', listUrl('regions', queries));
  }

  createRegion(payload: RegionPayload): Promise<Region> {
    return this._methodData('post', 'regions', payload);
  }

  updateRegion(id: number, payload: RegionPayload): Promise<Region> {
    return this._methodData('patch', `regions/${id}`, payload);
  }

  archiveRegion(id: number): Promise<Region> {
    return this._methodData('post', `regions/${id}/archive`);
  }

  unarchiveRegion(id: number): Promise<Region> {
    return this._methodData('post', `regions/${id}/unarchive`);
  }

  // Deletes the region and all its previous versions: CRM admins only
  deleteRegion(id: number): Promise<void> {
    return this._methodData('delete', `regions/${id}`);
  }

  // The changes of every region version, the most recent first, by pages
  getRegionChanges(queries: Queries = {}): Promise<Page<RegionChange>> {
    return this._methodData('get', `regions/changes?${new URLSearchParams(queries)}`);
  }

  // The changes of a region version and of all its predecessors up to the first version, the most recent first
  getRegionLineage(id: number): Promise<RegionChange[]> {
    return this._methodData('get', `regions/${id}/changes`);
  }

  getDepartments(queries: Queries = {}): Promise<Page<Department>> {
    return this._methodData('get', listUrl('departments', queries));
  }

  getFlux(queries: Queries = {}): Promise<Page<Flux>> {
    return this._methodData('get', listUrl('flux', queries));
  }
}
